import React from 'react';
import AntConfigProvider from 'antd-core/es/config-provider';
import { useTheme } from '@10d/tend-ui-theme';
import { Box } from '@10d/tend-ui-grid';
import { Dot } from '@10d/tend-ui/ui';
import { Counter } from '@10d/tend-ui/primitives';
import { ChevronDown } from '@10d/tend-ui-icons/ChevronDown';
import { ChevronUp } from '@10d/tend-ui-icons/ChevronUp';
import merge from 'lodash/merge';
import { Root } from './styled.js';

const Label = ({ after, children, badge, }) => {
    switch (badge === null || badge === void 0 ? void 0 : badge.type) {
        case 'dot': {
            return (React.createElement(Box, { as: 'span', "$display": 'flex', "$alignItems": 'center', "$justifyContent": 'space-between', "$gap": 4 },
                React.createElement("span", { className: 'tend-ui-menu-item-label' }, children),
                React.createElement(Dot, { color: 'blue700' }),
                after));
        }
        case 'counter': {
            return (React.createElement(Box, { as: 'span', "$display": 'flex', "$alignItems": 'center', "$justifyContent": 'space-between', "$gap": 4 },
                React.createElement("span", { className: 'tend-ui-menu-item-label' }, children),
                React.createElement(Counter, Object.assign({ backgroundColor: 'blue700', color: 'white800-transparent' }, badge)),
                after));
        }
        default:
            return (React.createElement(Box, { as: 'span', "$display": 'flex', "$alignItems": 'center', "$justifyContent": 'space-between', "$gap": 4 },
                React.createElement("span", { className: 'tend-ui-menu-item-label' }, children),
                after));
    }
};
const traverse = (menu, openedKeys) => {
    var _a;
    if (!menu)
        return menu;
    if ('type' in menu)
        return menu;
    if ('children' in menu) {
        const { badge } = menu;
        const isOpened = openedKeys.includes(menu.key);
        const after = isOpened ? React.createElement(ChevronUp, null) : React.createElement(ChevronDown, null);
        return Object.assign(Object.assign({}, menu), { popupOffset: [-8, 0], label: (React.createElement(Label, { after: after, badge: badge }, menu.label)), children: (_a = menu.children) === null || _a === void 0 ? void 0 : _a.map(menu => traverse(menu, openedKeys)) });
    }
    const { badge } = menu;
    return Object.assign(Object.assign({}, menu), { label: React.createElement(Label, { badge: badge }, menu.label) });
};
/**
 * @internal Не для публичного использования
 * TODO: Переписать на свою реализацию, antd очень ограничен
 */
const Navigation = ({ items = [], defaultSelectedKeys, selectedKeys, onSelect, styling, }) => {
    const theme = useTheme();
    const _styling = React.useMemo(() => merge({
        tabDefaultBg: theme.colors.blue600,
        tabActiveBg: theme.colors.blue700,
        tabHoverBg: theme.colors['gray100-transparent'],
        tabPressedBg: theme.colors['gray200-transparent'],
        tabActiveHoverBg: theme.colors.blue800,
        tabActivePressedBg: theme.colors.blue800,
        tabDefaultIcon: '',
        tabHoverIcon: '',
        defaultText: theme.colors.gray0,
        hoverText: theme.colors.gray0,
        pressedText: theme.colors.gray0,
        activeText: theme.colors.gray0,
        activeHoverText: theme.colors.gray0,
        activePressedText: theme.colors.gray0,
    }, styling), [styling, theme.colors]);
    const [openedKeys, setOpenedKeys] = React.useState([]);
    const overflowedIndicator = React.useMemo(() => {
        const isOpened = openedKeys.includes('rc-menu-more');
        return (React.createElement(Box, { as: 'span', "$display": 'flex', "$alignItems": 'center', "$gap": 4 },
            React.createElement("span", { className: 'tend-ui-menu-item-label' }, "\u0415\u0449\u0451"),
            isOpened ? React.createElement(ChevronUp, { size: 16 }) : React.createElement(ChevronDown, { size: 16 })));
    }, [openedKeys]);
    const handleOpenChange = React.useCallback((path) => {
        setOpenedKeys(path);
    }, []);
    const handleSelect = React.useCallback((info) => {
        onSelect === null || onSelect === void 0 ? void 0 : onSelect(info.keyPath);
    }, [onSelect]);
    return (React.createElement(AntConfigProvider, { theme: {
            components: {
                Menu: {
                    colorBgContainer: 'transparent',
                    itemHeight: 28,
                    itemHoverBg: theme.colors['gray50-transparent'],
                    itemActiveBg: theme.colors['gray50-transparent'],
                    itemSelectedBg: theme.colors['gray50-transparent'],
                    itemMarginBlock: 0,
                    itemMarginInline: 0,
                    subMenuItemBorderRadius: 0,
                    horizontalItemBorderRadius: 16,
                    groupTitleFontSize: 12,
                    groupTitleLineHeight: '16px',
                    groupTitleColor: theme.colors.gray650,
                    activeBarHeight: 0,
                },
            },
        } },
        React.createElement(Root, { "$theme": theme, "$styling": _styling, defaultSelectedKeys: defaultSelectedKeys, selectedKeys: selectedKeys, mode: 'horizontal', expandIcon: null, items: items.map(item => traverse(item, openedKeys)), onSelect: handleSelect, overflowedIndicator: overflowedIndicator, onOpenChange: handleOpenChange, triggerSubMenuAction: 'click' })));
};
Navigation.displayName = 'Header.Navigation';

export { Navigation };
